import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../Components/Header/Header';
import BillingToggle from '../../Components/Pricing/BillingToggle';
import PricingCard from '../../Components/Pricing/PricingCard';
import AddOnCard from '../../Components/Pricing/AddOnCard';
import PricingSummary from '../../Components/Pricing/PricingSummary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSlidersH, faShieldAlt, faSyncAlt, faHeart } from '@fortawesome/free-solid-svg-icons';
import './Pricing.css';
const API_BASE = window.HMI_API_BASE || process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';
export default function Pricing() {
  const {
    t
  } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [isYearly, setIsYearly] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // Load plans & addons from the public API

  useEffect(() => {
    const token = localStorage.getItem('HMI_AUTH_TOKEN');
    fetch(`${API_BASE}/public/pricing`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      }
    }).then(res => res.json()).then(result => {
      if (result.success) {
        setPlans(result.data.plans);
        setAddons(result.data.addons); // Set Personal as default selected plan

        const personal = result.data.plans.find(p => p.id === 'plan_personal');

        if (personal) {
          setSelectedPlan(personal);
        } else if (result.data.plans.length > 0) {
          setSelectedPlan(result.data.plans[0]);
        }
      } else {
        setError(result.error?.message || 'Failed to load pricing data');
      }

      setLoading(false);
    }).catch(err => {
      console.error('Error fetching pricing:', err);
      setError('Connection error loading pricing page');
      setLoading(false);
    });
  }, []);

  const handleSelectPlan = plan => {
    setSelectedPlan(plan);
  };

  const handleToggleAddon = addon => {
    setSelectedAddons(prev => {
      const exists = prev.some(item => item.id === addon.id);

      if (exists) {
        return prev.filter(item => item.id !== addon.id);
      } else {
        return [...prev, addon];
      }
    });
  };

  const handleCheckout = selection => {
    if (selection.plan.type === 'Custom') {
      window.location.href = '/support?subject=Sales%20Inquiry';
    } else {
      alert(`Checkout ready! Selected Plan: ${selection.plan.name} (${selection.isYearly ? 'Yearly' : 'Monthly'})\nAdd-ons: ${selection.addons.map(a => a.name).join(', ') || 'None'}\nEstimated Total: $${(selection.isYearly ? selection.totals.yearly : selection.totals.monthly).toFixed(2)}\n\nThis is a mock checkout flow. Real Stripe integration will be configured in the next phase.`);
    }
  };

  if (loading) {
    return <>
        <Header />
        <div className="pricing-page-container" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
          <h3>{t("Loading pricing options...")}</h3>
        </div>
      </>;
  }

  if (error) {
    return <>
        <Header />
        <div className="pricing-page-container" style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 20
      }}>
          <h3 style={{
          color: '#df473c'
        }}>{error}</h3>
          <button onClick={() => window.location.reload()} className="plan-cta-btn" style={{
          width: 'auto'
        }}>{t("Try Again")}</button>
        </div>
      </>;
  }

  return <>
      <Header />
      <Helmet>
        <title>{t("Pricing & Plans - HeresMyInfo")}</title>
      </Helmet>

      <div className="pricing-page-container">
        {
        /* Header */
      }
        <div className="pricing-header">
          <h1>{t("Flexible pricing for any network")}</h1>
          <p>{t("Get started with basic sharing for free, or unlock advanced customization, priority support, and developer tools as you grow.")}</p>
        </div>

        {
        /* Toggle */
      }
        <BillingToggle isYearly={isYearly} onToggle={setIsYearly} />

        {
        /* Plans Grid */
      }
        <div className="plans-grid">
          {plans.map(plan => <PricingCard key={plan.id} plan={plan} isYearly={isYearly} onSelect={handleSelectPlan} />)}
        </div>

        {
        /* À la carte Addons */
      }
        <div className="alacarte-section">
          <h2>{t("\xC0 La Carte Services")}</h2>
          <div className="alacarte-intro">{t("Customize your setup with individual add-ons as your organization scales.")}</div>
          
          <div className="alacarte-grid">
            {addons.map(addon => <AddOnCard key={addon.id} addon={addon} isAdded={selectedAddons.some(item => item.id === addon.id)} onToggle={handleToggleAddon} />)}
          </div>
        </div>

        {
        /* Checkout Summary */
      }
        <PricingSummary selectedPlan={selectedPlan} selectedAddons={selectedAddons} isYearly={isYearly} onCheckout={handleCheckout} />

        {
        /* Trust Modules */
      }
        <div className="trust-section">
          <div className="trust-card">
            <div className="trust-icon-wrapper">
              <FontAwesomeIcon icon={faSlidersH} size="lg" />
            </div>
            <h3>{t("You're in control")}</h3>
            <p>{t("Adjust features, upgrade, downgrade, or cancel your subscription at any time without fees.")}</p>
          </div>

          <div className="trust-card">
            <div className="trust-icon-wrapper">
              <FontAwesomeIcon icon={faShieldAlt} size="lg" />
            </div>
            <h3>{t("Bank-level security")}</h3>
            <p>{t("All connection transfers, profiles, and transactions are protected by industry-standard encryption.")}</p>
          </div>

          <div className="trust-card">
            <div className="trust-icon-wrapper">
              <FontAwesomeIcon icon={faSyncAlt} size="lg" />
            </div>
            <h3>{t("Always accessible")}</h3>
            <p>{t("Sync profiles in real-time across iOS, Android, and Web instantly.")}</p>
          </div>

          <div className="trust-card">
            <div className="trust-icon-wrapper">
              <FontAwesomeIcon icon={faHeart} size="lg" />
            </div>
            <h3>{t("Loved by users")}</h3>
            <p>{t("Organize your network, maintain contacts effortlessly, and share info seamlessly.")}</p>
          </div>
        </div>
      </div>
    </>;
}